import {useRequest} from "./useRequest";
import {Dispatch, SetStateAction, useCallback, useRef} from "react";
import type { SuperAgentRequest } from "superagent";
import request from "../../util/SuperagentUtils";

/**
 * @prop requestUrl - The request url
 * @prop queryParams - The query parameters to be sent with the GET request
 * @prop shouldAbort - Dictates whether new requests should abort previous requests.
 */
export interface UseGetRequestParams {
  requestUrl: string;
  queryParams?: any;
  shouldAbort?: boolean;
}

/**
 * @prop data the response body from the GET request, or undefined if no request has completed successfully yet
 * @prop setData setter for data
 * @prop isLoading true while the most recently initiated request is in-flight; earlier concurrent requests are treated as stale/ignored for this flag
 * @prop setIsLoading setter for isLoading
 * @prop error the error thrown by the request, or undefined if no request has failed yet
 * @prop setError setter for error
 * @prop meta the superagent request object for the most recently initiated request while it is in-flight; cleared back to undefined once that request settles (success, error, or abort)
 * @prop setMeta setter for meta
 * @prop doRequest initiates the GET request with the provided URL and query parameters.
 */
export interface UseGetRequest<T> {
  data: T | undefined;
  setData: Dispatch<SetStateAction<T | undefined>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  error: Error | undefined;
  setError: Dispatch<SetStateAction<Error | undefined>>;
  meta: SuperAgentRequest | undefined;
  setMeta: Dispatch<SetStateAction<SuperAgentRequest | undefined>>;
  doRequest: () => void;
}

const useGetRequest = <T>({ requestUrl, queryParams, shouldAbort = false }: UseGetRequestParams): UseGetRequest<T> => {
  const {
    data,
    setData,
    isLoading,
    setIsLoading,
    error,
    setError,
    meta,
    setMeta,
  } = useRequest();

  /**
   * Tracks the ID of the most recently initiated request for this hook instance.
   * - Used to discard responses from older in-flight requests when a newer one has already started.
   * - A ref is used (not state) so incrementing it never causes a re-render and
   * the latest value is always accessible inside async callbacks via closure.
   */
  const activeRequestIdRef = useRef(0);

  /**
   * Tracks the most recent in-flight request synchronously.
   * - Used to abort older requests when a new request starts (if shouldAbort is true).
   */
  const currentRequestRef = useRef<SuperAgentRequest | undefined>(undefined);

  /**
   * Initiates the GET request with the provided URL and query parameters.
   */
  const doRequest = useCallback(() => {
    // ID that tracks this request as the active (most recent) one
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;

    // Abort the previous request if it's still in-flight, and we're configured to do so
    if (shouldAbort) {
      currentRequestRef.current?.abort?.();
    }

    // setup request
    const req = request.get(requestUrl).query(queryParams);
    currentRequestRef.current = req;

    // expose the active request for advanced consumers (e.g. direct abort)
    setMeta(req);

    // reset error and start loading
    setError(undefined);
    setIsLoading(true);

    /**
     * Clean up logic. Only applies to most recent request tracked by {@link activeRequestIdRef}
     *
     * All older in-flight requests will have their callbacks silently discarded.
     * This ensures that a slow or aborted request can never flip isLoading to false while a newer request is still in flight.
     */
    const clearActiveRequest = (requestId: number) => {
      if (activeRequestIdRef.current !== requestId) return;
      currentRequestRef.current = undefined;
      setMeta(undefined);
      setIsLoading(false);
    }

    req.then(response => {
      // Primary guard: a newer request has already started — discard this response
      if (activeRequestIdRef.current !== requestId) return;
      setData(response.body);
      clearActiveRequest(requestId);
    }).catch((reason: any) => {
      // Primary guard: a newer request has already started — discard this error.
      if (activeRequestIdRef.current !== requestId) return;

      // Secondary guard: don't treat an intentional superagent abort as a real error.
      // The request ID guard above already handles the abort race condition, but this
      // check remains as a safety net for cases where abort fires without a replacement
      // request (e.g., a future caller invoking meta.abort() directly).
      if (reason.message !== 'Aborted') {
        setError(reason);
      }
      clearActiveRequest(requestId);
    });
  }, [queryParams, requestUrl, setData, setError, setIsLoading, setMeta, shouldAbort]);

  return {
    data,
    setData,
    isLoading,
    setIsLoading,
    error,
    setError,
    meta,
    setMeta,
    doRequest
  };
}

export { useGetRequest };
